import React, { useEffect, useState } from "react";
import { useAuthedApi } from "@/api/useAuthedApi";
import { Switch } from "@/components/ui";

/** The user's opt-out of automatic conversation capture (spec §7.3, Path 1).
 *  Renders nothing while the feature is off (the route 404s) or unreadable:
 *  a toggle we cannot show the state of is worse than none. Saves on toggle,
 *  like any switch, and reverts if the write fails. */
export function ConversationCaptureSetting() {
  const { conversationKnowledge } = useAuthedApi();
  const [optOut, setOptOut] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationKnowledge) return;
    let cancelled = false;
    conversationKnowledge.getSettings().then(
      (s) => {
        // null = the feature is off: render nothing.
        if (!cancelled && s) setOptOut(s.opt_out);
      },
      (e) => console.warn("conversation capture setting read failed", e),
    );
    return () => {
      cancelled = true;
    };
  }, [conversationKnowledge]);

  if (optOut === null) return null;

  const onChange = async (autoSave: boolean) => {
    const previous = optOut;
    setOptOut(!autoSave);
    setBusy(true);
    setError(null);
    try {
      const s = await conversationKnowledge.setSettings(!autoSave);
      setOptOut(s.opt_out);
    } catch (e) {
      setOptOut(previous);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-ph-scope="conversation_capture" className="py-6 first:pt-0 last:pb-0 space-y-3">
      <div className="space-y-1">
        <h3 className="text-[14px] font-semibold text-ink m-0">Conversations</h3>
        <p className="text-[12.5px] text-ink-muted m-0">
          Artie can keep a summary of a useful conversation in your personal knowledge, and what
          it learned, so it can use them later. You can still save any conversation yourself.
        </p>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-[13.5px] text-ink">
          Automatically save useful things from my conversations
        </span>
        <Switch
          checked={!optOut}
          onChange={(next) => void onChange(next)}
          label="Automatically save useful things from my conversations"
          disabled={busy}
          data-ph="toggle"
        />
      </div>
      {error && (
        <p role="alert" className="text-[12.5px] text-danger m-0">
          {error}
        </p>
      )}
    </section>
  );
}
